import React, { useState, useEffect, useRef } from 'react';
import { FaSignal, FaCheckCircle, FaPhoneSlash } from 'react-icons/fa';
import IconWrapper from './IconWrapper';
import { useNetworkStatus } from '../hooks/useNetworkStatus';
import { useI18n } from '../contexts/I18nContext';
import { useChild } from '../contexts/ChildContext';
import '../styles/OfflineIndicator.css';

type ModalType = 'offline' | 'recovered' | 'noData' | null;

/**
 * 離線狀態指示器
 * - 離線時：彈窗提醒，按確認可繼續使用
 * - 恢復連線時：彈窗提供立即重新載入或稍後更新
 * - 啟動時離線且無快取（無兒童資料）：顯示無法載入，須連線後重新整理
 */
export const OfflineIndicator: React.FC = () => {
    const { isOnline } = useNetworkStatus();
    const { t } = useI18n();
    const { children, loading } = useChild();
    const [modalType, setModalType] = useState<ModalType>(null);
    const prevOnlineRef = useRef(isOnline);

    // 連線狀態切換：離線時彈窗、恢復連線時彈窗
    useEffect(() => {
        const wasOnline = prevOnlineRef.current;
        prevOnlineRef.current = isOnline;

        if (wasOnline && !isOnline) {
            setModalType('offline');
        } else if (!wasOnline && isOnline) {
            setModalType('recovered');
        }
    }, [isOnline]);

    // 啟動時離線：有資料則顯示離線彈窗，無資料則顯示無法載入
    useEffect(() => {
        if (loading) return;
        if (!isOnline) {
            setModalType(children.length >= 1 ? 'offline' : 'noData');
        }
    }, [loading, isOnline, children.length]);

    // 恢復連線時，若有 noData 彈窗，改為 recovered（可重新載入）
    useEffect(() => {
        if (modalType === 'noData' && isOnline) {
            setModalType('recovered');
        }
    }, [modalType, isOnline]);

    const handleOfflineConfirm = () => {
        setModalType(null);
    };

    const handleRecoveredReload = () => {
        window.location.reload();
    };

    const handleRecoveredLater = () => {
        setModalType(null);
    };

    const handleNoDataReload = () => {
        window.location.reload();
    };

    if (modalType === null) return null;

    if (modalType === 'offline') {
        return (
            <div className="offline-modal-overlay">
                <div className="offline-modal">
                    <div className="offline-modal-header">
                        <h2>{t('offline.modal.title')}</h2>
                    </div>
                    <div className="offline-modal-content">
                        <div className="offline-icon-large" aria-hidden="true"><IconWrapper icon={FaSignal} size={48} style={{ color: 'var(--warning)' }} /></div>
                        <p className="offline-description">
                            {t('offline.modal.description')}
                        </p>
                        <div className="offline-modal-actions">
                            <button
                                className="offline-btn-primary"
                                onClick={handleOfflineConfirm}
                            >
                                {t('offline.modal.confirm')}
                            </button>
                        </div>
                    </div>
                </div>
            </div>
        );
    }

    if (modalType === 'recovered') {
        return (
            <div className="offline-modal-overlay">
                <div className="offline-modal">
                    <div className="offline-modal-header">
                        <h2>{t('offline.recovered.title')}</h2>
                    </div>
                    <div className="offline-modal-content">
                        <div className="offline-icon-large" aria-hidden="true"><IconWrapper icon={FaCheckCircle} size={48} style={{ color: 'var(--success)' }} /></div>
                        <p className="offline-description">
                            {t('offline.recovered.description')}
                        </p>
                        <div className="offline-modal-actions">
                            <button
                                className="offline-btn-primary"
                                onClick={handleRecoveredReload}
                            >
                                {t('offline.recovered.reload')}
                            </button>
                            <button
                                className="offline-btn-secondary"
                                onClick={handleRecoveredLater}
                            >
                                {t('offline.recovered.later')}
                            </button>
                        </div>
                    </div>
                </div>
            </div>
        );
    }

    // modalType === 'noData'
    return (
        <div className="offline-no-data-overlay">
            <div className="offline-modal">
                <div className="offline-modal-header">
                    <h2>{t('offline.noData.title')}</h2>
                </div>
                <div className="offline-modal-content">
                    <div className="offline-icon-large" aria-hidden="true"><IconWrapper icon={FaPhoneSlash} size={48} style={{ color: 'var(--danger)' }} /></div>
                    <p className="offline-description">
                        {t('offline.noData.description')}
                    </p>
                    <div className="offline-modal-actions">
                        <button
                            className="offline-btn-primary"
                            onClick={handleNoDataReload}
                        >
                            {t('offline.noData.reload')}
                        </button>
                    </div>
                </div>
            </div>
        </div>
    );
};
